/**
 * The seven diatonic modes.
 *
 * The rule this module exists to enforce, because it is the one everybody gets
 * wrong: **accidentals belong to the tonic, not to the mode name.** D Dorian has
 * no sharps or flats. E Dorian has F♯ and C♯ — the two-sharp collection of D
 * major, *not* the four sharps of E major. Saying "Dorian has the accidentals of
 * its own major key" is wrong, and it is wrong in a way that sounds plausible.
 *
 * So nothing here stores a set of accidentals per mode. Every spelling is
 * computed from tonic + degree formula, which cannot drift out of agreement with
 * the staff because it is what the staff is drawn from.
 */

// One table, in `pitch.ts`. A second copy here is exactly the "independent
// lookup table" the correctness gate forbids: the two can drift, and the
// drift shows up as a mode spelled differently from the staff it is drawn on.
import { LETTERS, MAJOR_SCALE_SEMITONES, naturalPitchClass, type Pitch } from './pitch';

export const MODE_IDS = [
  'ionian',
  'dorian',
  'phrygian',
  'lydian',
  'mixolydian',
  'aeolian',
  'locrian',
] as const;

export type ModeId = (typeof MODE_IDS)[number];

export interface ModeInfo {
  id: ModeId;
  name: string;
  /** Semitones above the tonic for degrees 1..7. */
  semitones: number[];
  /** Alterations against the parallel major, as displayed: '', '♭3', '♯4'… */
  formula: string[];
  /** W/H pattern between consecutive degrees, wrapping to the octave. */
  steps: ('W' | 'H')[];
  /** Which degrees give the mode its colour. 1-based. */
  characteristic: number[];
  /** Quality of the tonic triad built 1–3–5. */
  tonicTriad: 'major' | 'minor' | 'diminished';
  /** How far the mode's tonic sits above its parent major's tonic, in degrees. */
  degreeOfParent: number;
  /** Cause, not vibes: what actually makes it sound the way it does. */
  cause: string;
  /**
   * A common association, deliberately hedged.
   *
   * These are cultural conventions, not properties of the intervals. The wording
   * says so, because telling a learner that Phrygian *is* dark teaches them to
   * distrust their own ear when it disagrees.
   */
  association: string;
  /** Seventh chord made by 1–3–5–7, in lead-sheet form. */
  chordSuffix: string;
  /** Safe upper chord tones in the common jazz chord-scale convention. */
  tensions: { degree: 2 | 4 | 6; label: string; reason: string }[];
  /** Notes that need to move when held over the tonic seventh chord. */
  avoid: { degree: 2 | 4 | 6; label: string; clash: string; resolvesTo: 1 | 3 | 5 }[];
  /**
   * A tone whose status changes with the harmony around it.
   *
   * Avoid notes are a voicing convention, not a blacklist. Dorian's natural 6
   * is the clearest case: it is the mode's defining colour over a static vamp,
   * while many functional-harmony courses mark it as use-with-care on ii before
   * V7 because it forms the same tritone as the coming dominant. Keeping that
   * distinction in the domain prevents a screen from flattening two true ideas
   * into one false universal rule.
   */
  contextual: {
    degree: 2 | 4 | 6;
    label: string;
    inContext: 'functional';
    reason: string;
    practice: string;
  }[];
}

// The same major reference the interval namer measures against, so a mode's
// displayed formula and the interval names a learner reads cannot disagree.
const MAJOR = MAJOR_SCALE_SEMITONES.slice(0, 7);

function stepsOf(semitones: number[]): ('W' | 'H')[] {
  const wrapped = [...semitones, 12];
  return wrapped.slice(1).map((value, index) => (value - wrapped[index] === 2 ? 'W' : 'H'));
}

function formulaOf(semitones: number[]): string[] {
  return semitones.map((value, index) => {
    const difference = value - MAJOR[index];
    const degree = index + 1;
    if (difference === 0) return String(degree);
    if (difference === -1) return `♭${degree}`;
    if (difference === 1) return `♯${degree}`;
    return `${difference > 0 ? '♯'.repeat(difference) : '♭'.repeat(-difference)}${degree}`;
  });
}

function mode(
  id: ModeId,
  name: string,
  semitones: number[],
  characteristic: number[],
  tonicTriad: ModeInfo['tonicTriad'],
  degreeOfParent: number,
  cause: string,
  association: string,
  chordSuffix: string,
  tensions: ModeInfo['tensions'],
  avoid: ModeInfo['avoid'],
  contextual: ModeInfo['contextual'] = [],
): ModeInfo {
  return {
    id,
    name,
    semitones,
    formula: formulaOf(semitones),
    steps: stepsOf(semitones),
    characteristic,
    tonicTriad,
    degreeOfParent,
    cause,
    association,
    chordSuffix,
    tensions,
    avoid,
    contextual,
  };
}

export const MODES: Record<ModeId, ModeInfo> = {
  ionian: mode(
    'ionian', 'Ionian', [0, 2, 4, 5, 7, 9, 11], [3, 7], 'major', 1,
    'This is the major scale. Its 7 wants to move up to 1.',
    'It often feels bright and at home.',
    'maj7',
    [
      { degree: 2, label: '9', reason: 'It adds air above the root.' },
      { degree: 6, label: '13', reason: 'It adds warm color.' },
    ],
    [{ degree: 4, label: '11', clash: 'The 11 sits a half step above the 3.', resolvesTo: 3 }],
  ),
  dorian: mode(
    'dorian', 'Dorian', [0, 2, 3, 5, 7, 9, 10], [6], 'minor', 2,
    'It is minor, but its 6 is high. That 6 gives Dorian its sound.',
    'It often feels cool, strong, and open.',
    'm7',
    [
      { degree: 2, label: '9', reason: 'It opens the minor chord.' },
      { degree: 4, label: '11', reason: 'It rests well over the minor 3.' },
      { degree: 6, label: '13', reason: 'This is Dorian’s bright color note.' },
    ],
    [],
    [
      {
        degree: 6,
        label: '13',
        inContext: 'functional',
        reason: 'Before V7, the 13 and the minor 3 make the same tritone as the coming V7 chord.',
        practice: 'On a Dm7 to G7 loop, hear B over Dm7. Let it move to B on G7, or leave it out of the Dm7 voicing.',
      },
    ],
  ),
  phrygian: mode(
    'phrygian', 'Phrygian', [0, 1, 3, 5, 7, 8, 10], [2], 'minor', 3,
    'Its ♭2 is one half step above 1. You hear that close pull fast.',
    'It often feels dark and close.',
    'm7',
    [{ degree: 4, label: '11', reason: 'It adds color without a half-step rub.' }],
    [
      { degree: 2, label: '♭9', clash: 'The ♭9 sits a half step above the root.', resolvesTo: 1 },
      { degree: 6, label: '♭13', clash: 'The ♭13 sits a half step above the 5.', resolvesTo: 5 },
    ],
  ),
  lydian: mode(
    'lydian', 'Lydian', [0, 2, 4, 6, 7, 9, 11], [4], 'major', 4,
    'It is major with a ♯4. The ♯4 gives Lydian its lift.',
    'It often feels light and open.',
    'maj7',
    [
      { degree: 2, label: '9', reason: 'It adds air above the root.' },
      { degree: 4, label: '♯11', reason: 'This is Lydian’s bright color note.' },
      { degree: 6, label: '13', reason: 'It adds warm color.' },
    ],
    [],
  ),
  mixolydian: mode(
    'mixolydian', 'Mixolydian', [0, 2, 4, 5, 7, 9, 10], [7], 'major', 5,
    'It is major with a ♭7. The ♭7 gives it a blues sound.',
    'It often feels bold and loose.',
    '7',
    [
      { degree: 2, label: '9', reason: 'It adds air to the chord.' },
      { degree: 6, label: '13', reason: 'It adds a warm top note.' },
    ],
    [{ degree: 4, label: '11', clash: 'The 11 sits a half step above the 3.', resolvesTo: 3 }],
  ),
  aeolian: mode(
    'aeolian', 'Aeolian', [0, 2, 3, 5, 7, 8, 10], [6], 'minor', 6,
    'This is the natural minor scale. It has ♭3, ♭6, and ♭7.',
    'It often feels soft, sad, or calm.',
    'm7',
    [
      { degree: 2, label: '9', reason: 'It opens the minor chord.' },
      { degree: 4, label: '11', reason: 'It rests well over the minor 3.' },
    ],
    [{ degree: 6, label: '♭13', clash: 'The ♭13 sits a half step above the 5.', resolvesTo: 5 }],
  ),
  locrian: mode(
    'locrian', 'Locrian', [0, 1, 3, 5, 6, 8, 10], [2, 5], 'diminished', 7,
    'It has a ♭2 and a ♭5. The low 5 makes the home chord feel tense.',
    'It often feels tense and unsure.',
    'm7♭5',
    [
      { degree: 4, label: '11', reason: 'It gives the chord more space.' },
      { degree: 6, label: '♭13', reason: 'It adds the mode’s dark color.' },
    ],
    [{ degree: 2, label: '♭9', clash: 'The ♭9 sits a half step above the root.', resolvesTo: 1 }],
  ),
};

export const MODE_LIST: ModeInfo[] = MODE_IDS.map((id) => MODES[id]);

/**
 * Spell a mode on a tonic.
 *
 * One letter per degree, in order, with the accidental that makes the interval
 * correct. That is what keeps `F♯` from being written `G♭` in a scale that
 * already uses G — the same rule notation itself follows.
 */
export function spellMode(tonic: Pitch, modeId: ModeId): Pitch[] {
  const info = MODES[modeId];
  const startIndex = LETTERS.indexOf(tonic.letter);
  const tonicValue = (naturalPitchClass(tonic.letter) + tonic.alter + 120) % 12;

  return info.semitones.map((interval, degree) => {
    const letter = LETTERS[(startIndex + degree) % 7];
    const natural = naturalPitchClass(letter);
    const wanted = (tonicValue + interval) % 12;
    // Nearest signed distance, so an alteration is never written as +11.
    let alter = ((wanted - natural + 18) % 12) - 6;
    if (alter > 6) alter -= 12;
    if (alter < -6) alter += 12;
    const octave = tonic.octave + Math.floor((startIndex + degree) / 7);
    return { letter, alter, octave };
  });
}

/** The major scale this mode's notes are a rotation of. */
export function parentMajor(tonic: Pitch, modeId: ModeId): Pitch {
  const scale = spellMode(tonic, modeId);
  const info = MODES[modeId];
  // The parent tonic is the degree that makes this mode Ionian.
  const index = (7 - (info.degreeOfParent - 1)) % 7;
  const note = scale[index];
  return { ...note, octave: tonic.octave };
}

/** Accidentals in the spelled scale, in the order they appear. */
export function modeAccidentals(tonic: Pitch, modeId: ModeId): Pitch[] {
  return spellMode(tonic, modeId).filter((note) => note.alter !== 0);
}

/** The tonic triad, 1–3–5 of the mode. */
export function tonicTriad(tonic: Pitch, modeId: ModeId): Pitch[] {
  const scale = spellMode(tonic, modeId);
  return [scale[0], scale[2], scale[4]];
}

/** The tonic seventh chord, 1–3–5–7. */
export function tonicSeventh(tonic: Pitch, modeId: ModeId): Pitch[] {
  const scale = spellMode(tonic, modeId);
  return [scale[0], scale[2], scale[4], scale[6]];
}

/**
 * Which degrees differ between two modes on the same tonic.
 *
 * The point of comparison practice: holding the tonic still and changing one
 * note is how a learner hears what a mode actually *is*, rather than memorising
 * it as a rotation of some other scale.
 */
export function compareModes(a: ModeId, b: ModeId): number[] {
  const left = MODES[a].semitones;
  const right = MODES[b].semitones;
  const differing: number[] = [];
  for (let degree = 0; degree < 7; degree += 1) {
    if (left[degree] !== right[degree]) differing.push(degree + 1);
  }
  return differing;
}
